import { Resolver } from '../../interfaces/Resolver';
import firebase from 'firebase';
import { withTimeout } from '../../../helpers/loading';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);


export interface FirebaseItemTranslator<T> {
    translate(
        snapshot: firebase.database.DataSnapshot,
        onSuccess: (T: T) => void,
        onFailure: (string) => void
    ): void | Promise<void>;
}

export function dateFromFirebaseString(dateString): Date {
    const dayJsobject = dayjs(dateString, 'YYYY-MM-DD HH:mm:ss Z');
    return dayJsobject.toDate();
}

export function dateFromFirebaseDateTimeWithTimezone(dateString, timezone): Date {
    const normalizedTime = dayjs.tz(dateString, timezone).utc().toDate();
    return normalizedTime;
}

export class FirebaseItemResolver<T> implements Resolver<T> {
    id: string;
    hasResolved: boolean;
    resolvedObject?: T;
    private inFlight?: Promise<T>;

    databaseReference: firebase.database.Reference;
    itemTranslator: FirebaseItemTranslator<T>;

    constructor(id, databaseReference, itemTranslator: FirebaseItemTranslator<T>, private initialSnapshot?: firebase.database.DataSnapshot) {
        this.id = id;
        this.hasResolved = false;
        this.databaseReference = databaseReference;
        this.itemTranslator = itemTranslator;
    }

    resolve(onSuccess: (resolvedObject: T) => void, onFailure: (errorMessage: string) => void) {
        this.asAPromise().then(onSuccess, error => onFailure(String(error)));
    }

    cancel(token: any) {
        if (token) {
            this.databaseReference.off('value', token);
        }
    }

    asAPromise(): Promise<T> {
        if (this.inFlight) return this.inFlight;
        const request = new Promise<T>((resolve, reject) => {
            const snapshot = this.initialSnapshot;
            this.initialSnapshot = undefined;
            (snapshot ? Promise.resolve(snapshot) : this.databaseReference.once('value')).then(snapshot => {
                // Translators use callbacks, but some also return an async promise.
                // Observe both synchronous throws and asynchronous rejections.
                return this.itemTranslator.translate(snapshot, resolve, reject);
            }).catch(reject);
        });
        this.inFlight = withTimeout(request).then(item => {
            this.resolvedObject = item;
            this.hasResolved = true;
            return item;
        }).finally(() => {
            // Only share pending work: subsequent reads must see edits and live scores.
            this.inFlight = undefined;
        });
        return this.inFlight;
    }
}
